import { useEffect, useMemo, useRef, useState } from 'react';
import { TALK_PREFIX } from '@rabbitfuel/shared';
import { useRealtime } from './realtime.tsx';
import { Publisher, Subscriber } from './webrtc.ts';

/**
 * Gegensprechen zwischen Regie und Handy -- wie FaceTime, nur dass das
 * Gespraech in den Stream gehen kann.
 *
 * Die eine Richtung gibt es schon: die Regie abonniert jedes freigegebene
 * Handy fuer ihre Vorschau, dessen Ton liegt also ohnehin vor und muss nur
 * abgespielt werden. Neu ist die andere Richtung, die **Sprechleitung**: eine
 * eigene Verbindung von der Regie zum Handy, nur Ton, kein Bild.
 *
 * Sie ist bewusst keine Quelle. Eine Quelle stuende in jeder Liste, liesse
 * sich ins Hauptbild schalten und ginge damit in den Stream -- die
 * Sprechleitung soll genau das nicht. Sie nutzt nur dieselben Bausteine
 * (Publisher, Subscriber, `rtc:signal`), unter der Kennung
 * `talk:<Verbindung der Regie>` statt einer Quellen-ID.
 */

export interface TalkLine {
  /** Verbindung des Handys, das die Regie hoeren soll. */
  connectionId: string;
  /**
   * Ob die Regie auf dieser Leitung gerade zu hoeren ist. Waehrend eines
   * Vorgespraechs mit einem anderen Handy nicht -- das bleibt unter vier Augen.
   */
  live: boolean;
}

interface OpenLine {
  publisher: Publisher;
  /** Eigene Kopie des Mikrofons -- so laesst sie sich je Leitung schalten. */
  track: MediaStreamTrack;
  /** Woraus die Leitung gebaut wurde; aendert sich das, wird neu gebaut. */
  mic: MediaStreamTrack;
  talkId: string;
}

/** Wie oft eine abgerissene Leitung neu angeboten wird. */
const RETRY_MS = 3000;

/**
 * Regie-Seite: haelt je Handy eine Sprechleitung offen.
 *
 * Jede Leitung bekommt ihren eigenen Klon des Mikrofons. Nur so kann ein
 * Vorgespraech („gleich bist du live") genau ein Handy erreichen, waehrend
 * das Handy im Bild es nicht hoert -- `enabled` gilt je Spur, und ein Klon
 * ist eine eigene Spur.
 *
 * Ohne Mikrofon (die Regie hat keine Kamera und keinen Bildschirm laufen)
 * gibt es keine Leitung: die Regie hoert dann nur zu.
 */
export function useTalkLines(mic: MediaStreamTrack | null, lines: TalkLine[]) {
  const { state, send, on } = useRealtime();
  const myConnId = state.self?.connectionId ?? null;
  const iceServers = state.iceServers;
  const iceKey = JSON.stringify(iceServers);
  const openRef = useRef(new Map<string, OpenLine>());
  const talkId = myConnId ? `${TALK_PREFIX}${myConnId}` : null;

  // Antworten und Kandidaten der Handys -- jede Leitung kennt nur ihre Gegenstelle.
  useEffect(
    () =>
      on((msg) => {
        if (msg.t !== 'rtc:signal') return;
        for (const line of openRef.current.values()) line.publisher.handleSignal(msg.from, msg.data);
      }),
    [on],
  );

  const wantedKey = lines
    .map((l) => l.connectionId)
    .sort()
    .join('|');

  useEffect(() => {
    const open = openRef.current;
    const close = (connectionId: string) => {
      const line = open.get(connectionId);
      if (!line) return;
      // Auflegen ansagen -- sonst merkt das Handy es erst, wenn ICE aufgibt.
      send({ t: 'rtc:signal', to: connectionId, data: { lane: 'down', sourceId: line.talkId, kind: 'bye' } });
      line.publisher.closeAll();
      line.track.stop();
      open.delete(connectionId);
    };

    const wanted = new Set(mic && talkId && wantedKey ? wantedKey.split('|') : []);
    for (const [connectionId, line] of [...open]) {
      if (!wanted.has(connectionId) || line.mic !== mic || line.talkId !== talkId) close(connectionId);
    }
    if (!mic || !talkId) return;
    for (const connectionId of wanted) {
      if (open.has(connectionId)) continue;
      const track = mic.clone();
      const publisher = new Publisher((to, data) => send({ t: 'rtc:signal', to, data }), iceServers, talkId);
      publisher.setStream(new MediaStream([track]));
      void publisher.addSubscriber(connectionId);
      open.set(connectionId, { publisher, track, mic, talkId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantedKey, mic, talkId, iceKey, send]);

  // Wer auf welcher Leitung zu hoeren ist.
  const liveKey = lines.map((l) => `${l.connectionId}:${l.live ? 1 : 0}`).join('|');
  useEffect(() => {
    const live = new Map(lines.map((l) => [l.connectionId, l.live]));
    for (const [connectionId, line] of openRef.current) line.track.enabled = live.get(connectionId) ?? false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveKey, wantedKey, mic, talkId]);

  /*
   * Eine gescheiterte Verbindung raeumt der Publisher selbst ab (Funkloch,
   * Netzwechsel am Handy). Die Leitung soll aber stehen, solange das Handy im
   * Gespraech ist -- also neu anbieten.
   */
  useEffect(() => {
    const timer = window.setInterval(() => {
      for (const [connectionId, line] of openRef.current) {
        if (line.publisher.subscriberCount === 0) void line.publisher.addSubscriber(connectionId);
      }
    }, RETRY_MS);
    return () => window.clearInterval(timer);
  }, []);

  // Beim Verlassen der Regie auflegen.
  useEffect(
    () => () => {
      for (const [connectionId, line] of openRef.current) {
        send({ t: 'rtc:signal', to: connectionId, data: { lane: 'down', sourceId: line.talkId, kind: 'bye' } });
        line.publisher.closeAll();
        line.track.stop();
      }
      openRef.current.clear();
    },
    [send],
  );
}

/**
 * Handy-Seite: nimmt Sprechleitungen der Regie an und gibt deren Ton heraus.
 *
 * Angenommen wird nur, was der Server als Regie ausweist (`fromRole`) --
 * weitergereicht wird sonst zwischen beliebigen Verbindungen eines Events,
 * und ein Handy soll nicht abspielen, was ihm irgendwer schickt.
 */
export function useTalkReceiver(): MediaStream[] {
  const { state, send, on } = useRealtime();
  const iceKey = JSON.stringify(state.iceServers);
  const subscriberRef = useRef<Subscriber | null>(null);
  /** Sprechleitung -> Verbindung der Regie, die sie gerade fuehrt. */
  const sendersRef = useRef(new Map<string, string>());
  const [streams, setStreams] = useState<Record<string, MediaStream>>({});

  useEffect(() => {
    const senders = sendersRef.current;
    const subscriber: Subscriber = new Subscriber(
      (to, data) => send({ t: 'rtc:signal', to, data }),
      state.iceServers,
      (id, stream) => setStreams((prev) => ({ ...prev, [id]: stream })),
      (id) =>
        setStreams((prev) => {
          if (!(id in prev)) return prev;
          const next = { ...prev };
          delete next[id];
          return next;
        }),
      (id, peerState) => {
        // Kein Standbild wie bei einer Kamera: eine tote Leitung ist still,
        // und die Anzeige „Regie spricht mit dir" soll dann auch weg sein.
        if (peerState !== 'failed' && peerState !== 'closed') return;
        senders.delete(id);
        subscriber.forget(id);
      },
    );
    subscriberRef.current = subscriber;
    return () => {
      subscriber.closeAll();
      subscriberRef.current = null;
      senders.clear();
      setStreams({});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iceKey, send]);

  useEffect(
    () =>
      on((msg) => {
        if (msg.t !== 'rtc:signal' || msg.fromRole !== 'organizer') return;
        const data = msg.data;
        if (data.lane !== 'down' || !data.sourceId.startsWith(TALK_PREFIX)) return;
        const subscriber = subscriberRef.current;
        if (!subscriber) return;
        const id = data.sourceId;
        if (data.kind === 'bye') {
          sendersRef.current.delete(id);
          subscriber.forget(id);
          return;
        }
        // Neue Leitung, oder dieselbe Regie aus einer neuen Verbindung.
        if (sendersRef.current.get(id) !== msg.from) {
          sendersRef.current.set(id, msg.from);
          subscriber.rebind(id, msg.from);
        }
        subscriber.handleSignal(msg.from, data);
      }),
    [on],
  );

  return useMemo(() => Object.values(streams), [streams]);
}
